import { forwardRef, useMemo } from "react";

import { Overflow } from "../overflow/Overflow";
import { OverflowItem } from "../overflow/OverflowItem";
import { Tooltip } from "../tooltip/Tooltip";
import { Tag } from "./Tag";

import type { CollectionBase, Key } from "@react-types/shared";
import type { ForwardedRef, ReactElement, ReactNode } from "react";
import type { MultiSelectListState } from "../../hooks/useMultiSelectListState";
import type { SelectAllOptions } from "../listbox/ListBox";
import type { OverflowProps } from "../overflow/Overflow";

export interface RenderTagProps {
  /** The unique key for this item */
  key: Key;
  /** The text value of this item */
  textValue?: string;
  /** Whether the tag can be removed (always true for multi-select) */
  closable: boolean;
  /** Handler to call when the tag should be removed */
  onClose: () => void;
  /** The rendered label/children of this item */
  label?: ReactNode;
}

export interface MultiSelectTagsProps<T>
  extends Omit<OverflowProps<T>, keyof CollectionBase<T> | "renderRest"> {
  state: MultiSelectListState<T>;
  selectAll?: SelectAllOptions;
  /**
   * Custom function to render each tag. If not provided, uses default Tag component.
   */
  renderTag?: (props: RenderTagProps) => ReactNode;
}

const MultiSelectTagsImpl = <T extends object>(
  {
    state,
    selectAll: { isEnabled: isSelectAllEnabled, label: selectAllLabel = "All Items" } = {
      isEnabled: false,
    },
    renderTag,
    ...props
  }: MultiSelectTagsProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const items = useMemo(() => {
    if (isSelectAllEnabled && state.selectionManager.isSelectAll) {
      const onClose = () => state.selectionManager.toggleSelectAll();
      const tagContent = renderTag ? (
        renderTag({
          key: "select-all",
          textValue: selectAllLabel,
          closable: true,
          onClose,
          label: selectAllLabel,
        })
      ) : (
        <Tag size="sm" className="mr-1" onRemove={onClose}>
          {selectAllLabel}
        </Tag>
      );
      return [
        <OverflowItem key="select-all" textValue={selectAllLabel}>
          {tagContent}
        </OverflowItem>,
      ];
    }

    return (state.selectedItems ?? []).map(item => {
      const onClose = () => state.selectionManager.toggleSelection(item.key);
      const tagContent = renderTag ? (
        renderTag({
          key: item.key,
          textValue: item.textValue,
          closable: true,
          onClose,
          label: item.textValue,
        })
      ) : (
        <Tag size="sm" className="mr-1" onRemove={onClose}>
          {item.textValue}
        </Tag>
      );
      return (
        <OverflowItem key={item.key} textValue={item.textValue}>
          {tagContent}
        </OverflowItem>
      );
    });
  }, [state, isSelectAllEnabled, selectAllLabel, renderTag]);

  return (
    <Overflow
      renderRest={omittedItems => (
        <Tooltip
          color="default"
          content={
            <div className="flex flex-col gap-1">
              {omittedItems.map(item => (
                <span key={item.index}>{item.textValue || item.rendered}</span>
              ))}
            </div>
          }
        >
          <Tag size="sm">+{omittedItems.length}</Tag>
        </Tooltip>
      )}
      {...props}
      ref={ref}
    >
      {items}
    </Overflow>
  );
};

/**
 * @internal
 */
export const MultiSelectTags = forwardRef(MultiSelectTagsImpl) as <T extends object>(
  props: MultiSelectTagsProps<T> & { ref?: ForwardedRef<HTMLDivElement> }
) => ReactElement<unknown>;
